/**
 * useDiscounts Hook
 *
 * Fetches discounts from the Rails API for use in price modifier dropdowns.
 * Defaults to per_page=100 (Listable max) so the full catalog is available
 * up-front for client-side filtering.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { discountsQueryKeys, getDiscounts } from '../api/discounts';
import type { DiscountsListResponse, FetchDiscountsParams } from '../api/discounts';

const STALE_TIME = 30_000;
const DEFAULT_PER_PAGE = 100;

interface UseDiscountsOptions {
  params?: FetchDiscountsParams;
  initialData?: DiscountsListResponse;
}

export function useDiscounts({ params, initialData }: UseDiscountsOptions = {}) {
  const mergedParams: FetchDiscountsParams = { per_page: DEFAULT_PER_PAGE, ...params };

  return useQuery({
    queryKey: discountsQueryKeys.list(mergedParams),
    queryFn: () => getDiscounts(mergedParams),
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData, initialDataUpdatedAt: Date.now() } : {}),
  });
}
